
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface SystemHealthMetric {
  id: string;
  component_name: string;
  status: 'healthy' | 'warning' | 'error' | 'maintenance';
  last_check_at: string;
  response_time_ms?: number;
  error_count: number;
  uptime_percentage: number;
  metadata: Record<string, any>;
  created_at: string;
}

interface ProcessingOptimization {
  id: string;
  user_id: string;
  optimization_type: string;
  current_value: number;
  target_value: number;
  improvement_percentage?: number;
  recommendations: any[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface HealthData {
  overall_status: string;
  components_status: {
    healthy: number;
    warning: number;
    error: number;
    maintenance: number;
  };
  last_updated: string;
}

// Mock data for demonstration
const mockHealthMetrics: SystemHealthMetric[] = [
  {
    id: '1',
    component_name: 'API Gateway',
    status: 'healthy',
    last_check_at: new Date().toISOString(),
    response_time_ms: 45,
    error_count: 0,
    uptime_percentage: 99.9,
    metadata: { region: 'us-east-1' },
    created_at: new Date().toISOString(),
  },
  {
    id: '2',
    component_name: 'Database',
    status: 'healthy',
    last_check_at: new Date().toISOString(),
    response_time_ms: 12,
    error_count: 0,
    uptime_percentage: 99.95,
    metadata: { connections: 25 },
    created_at: new Date().toISOString(),
  },
  {
    id: '3',
    component_name: 'AI Processing',
    status: 'warning',
    last_check_at: new Date().toISOString(),
    response_time_ms: 1200,
    error_count: 2,
    uptime_percentage: 98.5,
    metadata: { queue_length: 15 },
    created_at: new Date().toISOString(),
  },
];

const mockAIProviderHealth = [
  {
    id: '1',
    provider_name: 'OpenAI',
    endpoint: 'https://api.openai.com',
    status: 'healthy',
    response_time_ms: 350,
    error_rate: 0.1,
    last_error_at: null,
    error_details: null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }
];

const mockProcessingOptimizations: ProcessingOptimization[] = [
  {
    id: '1',
    user_id: 'user-1',
    optimization_type: 'cache',
    current_value: 75,
    target_value: 90,
    improvement_percentage: 20,
    recommendations: ['Increase cache TTL', 'Optimize cache keys'],
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  }
];

export const useSystemHealth = () => {
  const queryClient = useQueryClient();
  const [isPerformingCheck, setIsPerformingCheck] = useState(false);

  const {
    data: healthMetrics = mockHealthMetrics,
    isLoading: isLoadingHealth
  } = useQuery({
    queryKey: ['system-health'],
    queryFn: async () => {
      // Simulate API call with mock data
      await new Promise(resolve => setTimeout(resolve, 500));
      return mockHealthMetrics;
    },
    refetchInterval: 30000,
  });

  const {
    data: aiProviderHealth = mockAIProviderHealth,
    isLoading: isLoadingAIHealth
  } = useQuery({
    queryKey: ['ai-provider-health'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 300));
      return mockAIProviderHealth;
    },
    refetchInterval: 60000,
  });

  const {
    data: processingOptimizations = mockProcessingOptimizations,
    isLoading: isLoadingOptimizations
  } = useQuery({
    queryKey: ['processing-optimizations'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 400));
      return mockProcessingOptimizations;
    },
  });

  const updateHealthMetric = useMutation({
    mutationFn: async ({ 
      component_name, 
      status, 
      response_time_ms, 
      error_count = 0,
      uptime_percentage = 100,
      metadata = {} 
    }: {
      component_name: string;
      status: string;
      response_time_ms?: number;
      error_count?: number;
      uptime_percentage?: number;
      metadata?: Record<string, any>;
    }) => {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { component_name, status, response_time_ms, error_count, uptime_percentage, metadata };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-health'] });
    },
  });

  const performHealthCheck = async () => {
    setIsPerformingCheck(true);
    try {
      // Simulate health check
      await new Promise(resolve => setTimeout(resolve, 2000));
      queryClient.invalidateQueries({ queryKey: ['system-health'] });
    } finally {
      setIsPerformingCheck(false);
    }
  };

  const triggerRecovery = async (componentName: string) => {
    // Simulate recovery action
    await new Promise(resolve => setTimeout(resolve, 1500));
    queryClient.invalidateQueries({ queryKey: ['system-health'] });
  };

  // Helper function to get overall system status
  const getSystemStatus = () => {
    if (isLoadingHealth) return 'checking';
    
    const criticalComponents = healthMetrics.filter(m => m.status === 'error');
    const warningComponents = healthMetrics.filter(m => m.status === 'warning');
    
    if (criticalComponents.length > 0) return 'error';
    if (warningComponents.length > 0) return 'warning';
    return 'healthy';
  };

  // Helper function to get component status summary
  const getComponentSummary = () => {
    const summary = {
      total: healthMetrics.length,
      healthy: healthMetrics.filter(m => m.status === 'healthy').length,
      warning: healthMetrics.filter(m => m.status === 'warning').length,
      error: healthMetrics.filter(m => m.status === 'error').length,
      maintenance: healthMetrics.filter(m => m.status === 'maintenance').length,
    };
    
    return summary;
  };

  // Generate derived data that SystemHealthDashboard expects
  const healthData: HealthData = {
    overall_status: getSystemStatus(),
    components_status: {
      healthy: healthMetrics.filter(m => m.status === 'healthy').length,
      warning: healthMetrics.filter(m => m.status === 'warning').length,
      error: healthMetrics.filter(m => m.status === 'error').length,
      maintenance: healthMetrics.filter(m => m.status === 'maintenance').length,
    },
    last_updated: new Date().toISOString()
  };

  const componentStatus = healthMetrics.map(metric => ({
    name: metric.component_name,
    status: metric.status,
    last_check: metric.last_check_at,
    response_time: metric.response_time_ms,
    uptime: metric.uptime_percentage,
    error_count: metric.error_count
  }));

  const recentChecks = healthMetrics
    .sort((a, b) => new Date(b.last_check_at).getTime() - new Date(a.last_check_at).getTime())
    .slice(0, 10)
    .map(metric => ({
      component: metric.component_name,
      status: metric.status,
      timestamp: metric.last_check_at,
      checked_at: metric.last_check_at, // Add this property for compatibility
      details: metric.metadata
    }));

  return {
    healthMetrics,
    aiProviderHealth,
    processingOptimizations,
    healthData,
    componentStatus,
    recentChecks,
    isLoading: isLoadingHealth,
    isLoadingHealth,
    isLoadingAIHealth,
    isLoadingOptimizations,
    isPerformingCheck,
    updateHealthMetric,
    performHealthCheck,
    triggerRecovery,
    getSystemStatus,
    getComponentSummary,
  };
};
